import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  documentId,
  writeBatch,
} from "firebase/firestore";
import {
  db,
  AreaManagementRef,
  QuickTalesRef,
} from "../../../firebase";
import * as sgMail from "@sendgrid/mail";
import { sendEmailOnCall } from "../../../firebase";

export const getAllArea = async () => {
  try {
    // add order by position
    const queryRef = query(AreaManagementRef, orderBy("position"));

    const response = await getDocs(queryRef);

    // const response = await getDocs(AreaManagementRef);
    return response;
  } catch (error) {
    console.log("Error fetching data from Firestore:", error.message);
    return [];
  }
};
/**
 * Batch-fetch raw docs by id from a collection. Firestore `in` queries accept
 * up to 30 ids, so ids are de-duplicated, chunked, and the chunks run in
 * parallel. Returns a Map<id, data>. Used for lightweight counting (no
 * user/narrator/org resolution).
 */
export const getDocsByIds = async (collectionRef, ids) => {
  const uniqueIds = [
    ...new Set((ids || []).filter((id) => typeof id === "string" && id)),
  ];
  const result = new Map();
  const chunks = [];
  for (let i = 0; i < uniqueIds.length; i += 30) {
    chunks.push(uniqueIds.slice(i, i + 30));
  }
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const snap = await getDocs(
          query(collectionRef, where(documentId(), "in", chunk))
        );
        snap.forEach((d) => result.set(d.id, d.data()));
      } catch (error) {
        console.log("Error batch fetching docs:", error.message);
      }
    })
  );
  return result;
};

export const getOrgQuickTaleDocs = async (organizationId) => {
  try {
    const snap = await getDocs(
      query(QuickTalesRef, where("organization", "==", organizationId))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.log("Error fetching org quicktales:", error.message);
    return [];
  }
};

export const getOrgAreas = async (organizationId) => {
  try {
    const queryRef = query(
      AreaManagementRef,
      where("organization", "==", organizationId),
      orderBy("position")
    );
    const response = await getDocs(queryRef);
    // const response = await getDocs(AreaManagementRef);
    return response;
  } catch (error) {
    console.log("Error fetching data from Firestore:", error.message);
    return [];
  }
};

export const sendEmail2 = async (message) => {
  sgMail.setApiKey(process.env.REACT_APP_SENDGRID_KEY);

  sgMail
    .send(message)
    .then(() => {
      console.log("Email Sent!");
      return true;
    })
    .catch((error) => {
      console.error("Error: ", error);
      return false;
    });
};

export const sendgridEmail = async (message) => {
  let response = await sendEmailOnCall({ message });
  console.log("response", response.data.status);
  return response?.data?.status ? true : false;
};
export const getAreabyId = async (id) => {
  try {
    const response = await getDoc(doc(db, "area", id));
    return response;
  } catch (error) {
    console.log("Error fetching data from Firestore:", error.message);
    return [];
  }
};

export const getAreabyPosition = async (position) => {
  try {
    const queryRef = query(
      AreaManagementRef,
      where("position", "==", position)
    );
    const response = await getDocs(queryRef);
    const area = [];
    response.forEach((doc) => {
      area.push({ id: doc.id, ...doc.data() });
    });
    return area;

    // const response = await getDoc(
    //   doc(db, "area", where("position", "==", position))
    // );
    return response;
  } catch (error) {
    console.log("Error fetching data from Firestore:", error.message);
    return [];
  }
};

export const updateArea = async (id, areas) => {
  const areaDocRef = doc(db, "area", id);
  return await updateDoc(areaDocRef, areas);
};

export const addArea = async (data) => {
  const areaDocRef = collection(db, "area");
  return await addDoc(areaDocRef, data);
};

/**
 * Area names must be unique within an org. Comparison ignores case and
 * whitespace (the same normalization used for QuickCollect slugs), and
 * includes soft-deleted areas so a restore can't create a duplicate.
 * Returns the conflicting area, or null.
 */
export const findAreaNameConflict = (areas, name, excludeId) => {
  const target = normalizeQuickCollectSlug(name);
  if (!target) return null;
  return (
    (areas || []).find(
      (area) =>
        area?.id !== excludeId &&
        normalizeQuickCollectSlug(area?.name) === target
    ) || null
  );
};

/**
 * Create an area at a specific position among the non-deleted areas.
 * Every non-deleted area at or after that position is shifted down by one
 * and the new area is written in the same atomic batch.
 */
export const addAreaAtPosition = async (data, position, allAreas) => {
  const nonDeleted = filterNonDeletedAreas(allAreas)
    .slice()
    .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
  const target = Math.min(Math.max(1, position), nonDeleted.length + 1);

  const batch = writeBatch(db);
  // Renumber sequentially so any existing gaps/duplicates are cleaned up.
  nonDeleted.forEach((area, index) => {
    const newPosition = index + 1 >= target ? index + 2 : index + 1;
    if (area.position !== newPosition) {
      batch.update(doc(db, "area", area.id), { position: newPosition });
    }
  });
  const newRef = doc(collection(db, "area"));
  batch.set(newRef, { ...data, position: target });
  await batch.commit();
  return newRef;
};

/**
 * Soft-delete an area: it stays in Firestore but is marked inactive and
 * deleted so it is hidden from Area Management.
 */
export const softDeleteArea = async (id, updatedBy) =>
  await updateArea(id, {
    isActive: false,
    isDeleted: true,
    deletedAt: new Date(),
    deletedBy: updatedBy || "",
    updatedAt: new Date(),
    updatedBy: updatedBy || "",
  });

/**
 * Restore a soft-deleted area. It stays inactive so an admin can re-activate
 * it explicitly, and is moved to the end of the (non-deleted) position order.
 */
export const restoreArea = async (id, position, updatedBy) =>
  await updateArea(id, {
    isDeleted: false,
    position,
    restoredAt: new Date(),
    restoredBy: updatedBy || "",
    updatedAt: new Date(),
    updatedBy: updatedBy || "",
  });

/**
 * Only superadmins (role "super") may see and restore deleted areas.
 */
export const canViewDeletedAreas = (user) => user?.role === "super";

/**
 * Areas without an `isDeleted` field are treated as not deleted.
 */
export const isAreaDeleted = (area) => area?.isDeleted === true;

export const filterNonDeletedAreas = (areas) =>
  (areas || []).filter((area) => !isAreaDeleted(area));

export const deleteArea = async (id) => {
  const areaDocRef = doc(db, "area", id);
  return await deleteDoc(areaDocRef);
};

// ---------------------------------------------------------------------------
// QuickCollect — public per-Area guest submission links.
// See docs/QUICKCOLLECT_IMPLEMENTATION.md
// ---------------------------------------------------------------------------

/**
 * Normalize an area name into its URL slug: remove spaces, lowercase.
 * Must stay in sync with `normalizeQuickCollectSlug` in functions/quickCollect.js
 */
export const normalizeQuickCollectSlug = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

/**
 * Areas that are structural rather than real exhibitions cannot collect
 * submissions.
 */
export const isReservedAreaName = (name) => {
  const normalized = String(name || "").trim().toLowerCase();
  return normalized === "all" || normalized === "nearby";
};

/**
 * Build the public guest URL for an area.
 * Returns "" when we don't have enough information to build a valid link.
 */
export const buildQuickCollectLink = ({ orgHandle, slug }) => {
  const handle = String(orgHandle || "").trim();
  const areaSlug = normalizeQuickCollectSlug(slug);
  if (!handle || !areaSlug) return "";

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://admin.quicktales.com";

  return `${origin}/${handle}/${areaSlug}/submit`;
};

/**
 * Stamp the QuickCollect slug + approval mode onto an area.
 * Called when the admin generates the link from Area Management.
 */
export const enableQuickCollect = async ({
  areaId,
  areaName,
  autoApprove,
  updatedBy,
}) =>
  await updateArea(areaId, {
    quickCollectSlug: normalizeQuickCollectSlug(areaName),
    autoApprove: autoApprove === true,
    quickCollectUpdatedAt: new Date(),
    quickCollectUpdatedBy: updatedBy || "",
    updatedAt: new Date(),
    updatedBy: updatedBy || "",
  });

/**
 * Flip the approval mode for future submissions to this area.
 */
export const setAreaAutoApprove = async ({ areaId, autoApprove, updatedBy }) =>
  await updateArea(areaId, {
    autoApprove: autoApprove === true,
    quickCollectUpdatedAt: new Date(),
    quickCollectUpdatedBy: updatedBy || "",
    updatedAt: new Date(),
    updatedBy: updatedBy || "",
  });

export const areaSearch = (array, keyword) => {
  const searchTerm = keyword.toLowerCase();
  return array.filter((value) => {
    return (
      value.name && value.name.toLowerCase().match(new RegExp(searchTerm, "g"))
    );
  });
};

export const quickTalesSearch = (array, keyword) => {
  const searchTerm = keyword.toLowerCase();
  return array.filter((value) => {
    return (
      (value.title &&
        value.title.toLowerCase().match(new RegExp(searchTerm, "g"))) ||
      (value.createdBy &&
        value.createdBy.toLowerCase().match(new RegExp(searchTerm, "g")))
    );
  });
};
